import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MobileConfig, OUNode } from '../../types';
import { Icon } from './icons';

interface Props {
  value: string | null;
  configs: MobileConfig[];
  nodes: OUNode[];
  nodeId: string;
  onChange: (configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => void;
}

const MobileConfigPicker: React.FC<Props> = ({ value, configs, nodes, nodeId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = configs.find(c => c.id === value);

  const handleOpen = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
    }
    setOpen(v => !v);
  };

  const handleSelect = (configId: string | null) => {
    if (configId === value) { setOpen(false); return; }
    onChange(configId, null);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapRef.current && !wrapRef.current.contains(target) &&
        !(document.getElementById('picker-portal')?.contains(target))
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="picker-portal"
      className="picker-dropdown"
      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width, zIndex: 9999 }}
    >
      <div className="picker-list">
        {value && (
          <div className="picker-option" onClick={() => handleSelect(null)}>
            <span style={{ color: 'var(--color-dark-l3)', fontSize: 11 }}>✕</span>
            <span style={{ color: 'var(--color-dark-l2)' }}>Remove</span>
          </div>
        )}
        {configs.map(c => {
          const takenBy = nodes.find(n => n.id !== nodeId && n.mobileConfigId === c.id);
          const isSelected = value === c.id;
          const isInactive = !!takenBy && !isSelected;
          return (
            <div
              key={c.id}
              className={`picker-option ${isSelected ? 'selected' : ''}`}
              onClick={isInactive ? undefined : () => handleSelect(c.id)}
              style={isInactive ? { opacity: 0.38, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
            >
              {Icon.mobile}
              <span style={{ flex: 1 }}>{c.name}</span>
              {takenBy && !isSelected && (
                <span style={{ fontSize: 10, color: 'var(--color-dark-l3)', marginLeft: 4 }}>in use</span>
              )}
              {isSelected && <span className="check-icon">{Icon.check}</span>}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="picker-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        className={`picker-trigger ${!current ? 'empty' : ''}`}
        onClick={handleOpen}
      >
        {Icon.mobile}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'left' }}>
          {current ? current.name : 'Assign config'}
        </span>
        {Icon.chevronDown}
      </button>
      {dropdown}
    </div>
  );
};

export default MobileConfigPicker;
